// recordingNoticeGate.ts — CORE-1920
//
// Предупреждение о записи (CORE-1658) живёт на экране входа, а в комнату
// вели и другие дороги: прямая ссылка `/practice-rooms/session/<код>`, ссылка
// на сессию в чате, библиотека, «Мои комнаты», уведомления, анонс и ещё с
// десяток мест с `navigate('LiveRoomSession')`. Править каждое — значит
// пропустить следующее, поэтому проверка стоит в одной точке: в самой
// комнате, до входа (`/join` участника или `/spectate` гостя). Если в этой
// сессии предупреждение не подтверждено — человек уходит на экран входа, и
// «Войти» там возвращает его в комнату.
//
// «Подтверждено» здесь — отметка, которую экран входа ставит на устройстве
// после того, как сервер сохранил подтверждение. Отметка привязана к тому, кто
// подтверждал: гость, вошедший потом в аккаунт, для сервера — другой человек,
// и подтверждение у аккаунта своё.
//
// Чистые функции без RN-зависимостей — пиньятся в node-jest.

import { needsRecordingNotice, RECORDING_NOTICE_VERSION } from './recordingNotice';

/** Сколько отметок держать: старые сессии не нужны, а список растёт с каждой. */
export const RECORDING_ACK_LIMIT = 100;

/** Кто подтверждал: аккаунт или гость этого устройства. */
export function recordingAckIdentity(userId: string | null | undefined): string {
  return userId ? `user:${userId}` : 'guest';
}

/**
 * Комнату в маршрутах называют по-разному: код приглашения (ссылки, экран
 * входа) или числовой id (библиотека, «Мои комнаты», профиль мастера). Отметка
 * ставится на оба имени, чтобы любая дорога её нашла.
 */
function ackEntries(
  identity: string,
  room: { id?: number | string | null; inviteCode?: string | null },
  version: string,
): string[] {
  const refs = [room.id, room.inviteCode]
    .filter((r): r is number | string => r !== null && r !== undefined && String(r) !== '')
    .map(String);
  return Array.from(new Set(refs)).map((ref) => `${identity}|${ref}|${version}`);
}

/** Есть ли отметка у этого человека на эту комнату под текущей версией текста. */
export function hasRecordingAck(
  stored: readonly string[],
  identity: string,
  roomRefs: ReadonlyArray<number | string | null | undefined>,
  version: string = RECORDING_NOTICE_VERSION,
): boolean {
  return roomRefs.some((ref) =>
    ref !== null && ref !== undefined && String(ref) !== ''
    && stored.includes(`${identity}|${String(ref)}|${version}`));
}

/** Новый список отметок: свежие в конце, самые старые вытесняются. */
export function withRecordingAck(
  stored: readonly string[],
  identity: string,
  room: { id?: number | string | null; inviteCode?: string | null },
  version: string = RECORDING_NOTICE_VERSION,
  limit: number = RECORDING_ACK_LIMIT,
): string[] {
  const fresh = ackEntries(identity, room, version);
  const kept = stored.filter((e) => !fresh.includes(e));
  return [...kept, ...fresh].slice(-limit);
}

export type RecordingNoticeGate = 'enter' | 'notice';

/**
 * Пускать ли в комнату сразу или сначала на экран входа. Правило то же, что у
 * самого экрана (`needsRecordingNotice`): всем, кроме ведущего. Ведущий — по
 * загруженной комнате, а не по параметру маршрута: на вебе `isHost` лежит в
 * адресной строке. Завершённая сессия не предупреждает — входить уже некуда,
 * а её страница итогов должна открываться по той же ссылке.
 */
export function resolveRecordingNoticeGate(input: {
  isHost: boolean;
  ended: boolean;
  acknowledged: boolean;
}): RecordingNoticeGate {
  return needsRecordingNotice({ hasRoom: !input.ended, isHost: input.isHost, acknowledged: input.acknowledged })
    ? 'notice'
    : 'enter';
}

/**
 * Отказ сервера «нет подтверждения» (флаг `live_recording_consent_required`):
 * старые сборки и любые обходы получают его на `/join` или токене звонка.
 */
export function isRecordingConsentRequiredError(err: unknown): boolean {
  const e = err as { code?: unknown; status?: unknown } | null | undefined;
  return !!e && e.code === 'recording_consent_required' && e.status === 403;
}

/** Параметры экрана входа для комнаты, из которой человека развернули. */
export function recordingNoticeEntryParams(params: {
  roomId: string | number;
  roomTitle?: string;
  masterName?: string;
  mediaType?: string;
}): { roomId: string; roomTitle?: string; masterName?: string; mediaType?: string } {
  const out: { roomId: string; roomTitle?: string; masterName?: string; mediaType?: string } = {
    roomId: String(params.roomId),
  };
  if (params.roomTitle) out.roomTitle = params.roomTitle;
  if (params.masterName) out.masterName = params.masterName;
  if (params.mediaType) out.mediaType = params.mediaType;
  return out;
}
